import type { AgentView } from '@quintessence/core';
import { SERIES } from '../theme';

/** Value estimates (mean ± ensemble spread) for each action in the agent's latest decision. */
export function ActionValues({ agent }: { agent: AgentView }) {
  const t = agent.lastThought;
  if (!t) return <p className="empty">No decision yet.</p>;
  const maxAbs = Math.max(0.3, ...t.scores.map((s) => Math.abs(s.mean) + s.std));
  const pct = (v: number) => 50 + (v / maxAbs) * 50;
  return (
    <ul className="action-values" aria-label="Predicted value of each action">
      {t.scores.map((s) => {
        const chosen = s.action === t.size;
        const lo = pct(Math.min(0, s.mean));
        const hi = pct(Math.max(0, s.mean));
        return (
          <li key={s.action} className={`${chosen ? 'chosen' : ''} ${s.available ? '' : 'na'}`}>
            <span className="av-label">{s.action.toLowerCase()}</span>
            <span className="av-track">
              <i className="av-zero" />
              <span className="av-bar" style={{ left: `${lo}%`, width: `${Math.max(0.6, hi - lo)}%`, background: SERIES[agent.id], opacity: chosen ? 1 : 0.4 }} />
              <span className="av-whisker" style={{ left: `${pct(s.mean - s.std)}%`, width: `${pct(s.mean + s.std) - pct(s.mean - s.std)}%` }} />
            </span>
            <span className="av-value">
              {s.available ? `${s.mean >= 0 ? '+' : '−'}${Math.abs(s.mean).toFixed(2)}` : 'n/a'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Instinct (hand-written prior) vs learned (neural ensemble) share of the decision. */
export function InstinctMeter({ weight, color }: { weight: number; color: string }) {
  const learned = Math.round((1 - weight) * 100);
  return (
    <div className="instinct">
      <div className="instinct-labels">
        <span>Learned {learned}%</span>
        <span className="muted">Instinct {100 - learned}%</span>
      </div>
      <div className="instinct-track" role="meter" aria-valuenow={learned} aria-valuemin={0} aria-valuemax={100} aria-label="Share of decision from learned value network">
        <span style={{ width: `${learned}%`, background: color }} />
      </div>
    </div>
  );
}
